import type { AbstractIntlMessages } from 'use-intl';

import { getApiBase } from '@/lib/api';
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  pickLocaleFromAcceptLanguage,
  type SupportedLocale,
} from '@/i18n/config';
import { loadMessages } from '@/lib/i18n/messages';

export interface PublicChatInfo {
  tenantName: string;
  kbName: string;
  authMode: string;
  conversationStarters: string[];
  ipAllowed?: boolean | null;
}

/**
 * Client-side fetch of the public chat/widget config. The browser calls the API
 * itself, so the API settles the visitor's IP from its own ingress (the address
 * its trusted proxies forward); nothing has to be passed along from here.
 */
export async function fetchPublicChatInfo(slug: string): Promise<PublicChatInfo | null> {
  const res = await fetch(`${getApiBase()}/api/v1/public-chat/${encodeURIComponent(slug)}`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) return null;
  return res.json() as Promise<PublicChatInfo>;
}

/**
 * Public locale resolution (Story 24.4). Priority: `?locale=` embed override →
 * visitor `navigator.language(s)` → `DEFAULT_LOCALE`. Client counterpart of the
 * former server `resolvePublicLocale` (which read the `Accept-Language` header).
 */
export function resolvePublicLocale(searchLocale: string | undefined): SupportedLocale {
  if (isSupportedLocale(searchLocale)) return searchLocale;
  if (typeof navigator !== 'undefined') {
    const header = navigator.languages?.length ? navigator.languages.join(',') : navigator.language;
    if (header) return pickLocaleFromAcceptLanguage(header);
  }
  return DEFAULT_LOCALE;
}

export interface PublicChatLoaderData {
  info: PublicChatInfo;
  locale: SupportedLocale;
  messages: AbstractIntlMessages;
  token?: string;
}

/** Loader payload for the public chat/widget routes: config + i18n + token. */
export async function loadPublicChat(
  slug: string,
  searchStr: string
): Promise<PublicChatLoaderData | null> {
  const info = await fetchPublicChatInfo(slug);
  if (!info) return null;
  const params = new URLSearchParams(searchStr);
  const locale = resolvePublicLocale(params.get('locale') ?? undefined);
  const messages = await loadMessages(locale);
  return { info, locale, messages, token: params.get('token') ?? undefined };
}
